import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  StatGroup,
  Stat,
  StatLabel,
  StatNumber,
  Box,
  Flex,
  Text,
  Input,
  Button,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { fetchWithToken } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ChannelDetailModal = ({
  isOpen,
  onClose,
  channel,
  dateRange,
  accessToken,
  userId,
}) => {
  const { user } = useAuth();
  const [channelInfo, setChannelInfo] = useState(null);
  const [channelInfoLoading, setChannelInfoLoading] = useState(false);
  const [channelVideos, setChannelVideos] = useState([]);
  const [channelVideosPagination, setChannelVideosPagination] = useState(null);
  const [channelVideosLoading, setChannelVideosLoading] = useState(false);
  const [inputStartDate, setInputStartDate] = useState("");
  const [inputEndDate, setInputEndDate] = useState("");
  const [inputSearch, setInputSearch] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [channelVideosPage, setChannelVideosPage] = useState(1);

  useEffect(() => {
    if (isOpen && dateRange) {
      setInputStartDate(dateRange.start);
      setInputEndDate(dateRange.end);
      setSearchStartDate(dateRange.start);
      setSearchEndDate(dateRange.end);
      setInputSearch("");
      setSearchKeyword("");
      setChannelVideosPage(1);
    }
  }, [isOpen, dateRange]);

  useEffect(() => {
    if (!channel || !isOpen) return;
    const fetchChannelInfo = async () => {
      setChannelInfoLoading(true);
      try {
        const params = new URLSearchParams({
          channel_id: channel.channelId,
          start_date: searchStartDate || dateRange.start,
          end_date: searchEndDate || dateRange.end,
        });
        const res = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel/info?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await res.json();
        setChannelInfo(result.data);
      } catch (e) {
        setChannelInfo(null);
      } finally {
        setChannelInfoLoading(false);
      }
    };
    fetchChannelInfo();
  }, [channel, isOpen, searchStartDate, searchEndDate, accessToken, dateRange]);

  useEffect(() => {
    if (!channel || !isOpen) return;
    const fetchChannelVideos = async () => {
      setChannelVideosLoading(true);
      try {
        const params = new URLSearchParams({
          channel_id: channel.channelId,
          start_date: searchStartDate || dateRange.start,
          end_date: searchEndDate || dateRange.end,
          searchKeywords: searchKeyword,
          pageNumber: channelVideosPage,
          user_id: userId,
        });
        const res = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel/videos?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await res.json();
        setChannelVideos(result.data.shorts_list);
        setChannelVideosPagination(result.data.pagination);
      } catch (e) {
        setChannelVideos([]);
        setChannelVideosPagination(null);
      } finally {
        setChannelVideosLoading(false);
      }
    };
    fetchChannelVideos();
  }, [
    channel,
    isOpen,
    searchStartDate,
    searchEndDate,
    searchKeyword,
    channelVideosPage,
    accessToken,
    dateRange,
    userId,
  ]);

  const handleSearch = () => {
    setSearchStartDate(inputStartDate);
    setSearchEndDate(inputEndDate);
    setSearchKeyword(inputSearch);
    setChannelVideosPage(1);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={4}>
            {channel?.channelThumbnailUrl && (
              <img
                src={channel.channelThumbnailUrl}
                alt={channel.channelTitle}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )}
            <span>{channel?.channelTitle} 상세</span>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* 그래프 및 요약 */}
          {channelInfoLoading ? (
            <Flex align="center" justify="center" minH="200px">
              <Spinner />
            </Flex>
          ) : channelInfo ? (
            <>
              <StatGroup mb={6} gap={4}>
                <Stat px={6} py={4} bg="gray.50" borderRadius="xl" minW="180px">
                  <StatLabel fontSize="md" color="gray.600">
                    총 수익 (KRW)
                  </StatLabel>
                  <StatNumber fontSize="xl" color="black">
                    ₩
                    {channelInfo.channel_infor.estimated_revenue_krw?.toLocaleString() ??
                      0}
                  </StatNumber>
                </Stat>
                <Stat px={6} py={4} bg="gray.50" borderRadius="xl" minW="180px">
                  <StatLabel fontSize="md" color="gray.600">
                    총 수익 (USD)
                  </StatLabel>
                  <StatNumber fontSize="xl" color="black">
                    $
                    {channelInfo.channel_infor.estimated_revenue_usd?.toLocaleString() ??
                      0}
                  </StatNumber>
                </Stat>
                <Stat px={6} py={4} bg="gray.50" borderRadius="xl" minW="180px">
                  <StatLabel fontSize="md" color="gray.600">
                    총 영상 수
                  </StatLabel>
                  <StatNumber fontSize="xl" color="black">
                    {channelInfo.channel_infor.total_videos?.toLocaleString() ??
                      0}
                  </StatNumber>
                </Stat>
                <Stat px={6} py={4} bg="gray.50" borderRadius="xl" minW="180px">
                  <StatLabel fontSize="md" color="gray.600">
                    총 조회수
                  </StatLabel>
                  <StatNumber fontSize="xl" color="black">
                    {channelInfo.channel_infor.total_views?.toLocaleString() ??
                      0}
                  </StatNumber>
                </Stat>
              </StatGroup>
              <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
                {(() => {
                  const dailyStats =
                    channelInfo.dailyStats || channelInfo.daily_stats || [];
                  return (
                    <Chart
                      options={{
                        chart: {
                          id: "modal-revenue-views",
                          toolbar: { show: true },
                        },
                        xaxis: {
                          categories: dailyStats.map((d) => d.date),
                        },
                        colors: ["#1e90ff", "#00e676", "#ffc107"],
                        stroke: { width: 4 },
                        legend: { show: true, position: "bottom" },
                      }}
                      series={[
                        {
                          name: "수익(KRW)",
                          data: dailyStats.map((d) => d.revenue_krw),
                        },
                      ]}
                      type="line"
                      height={320}
                    />
                  );
                })()}
              </Box>
            </>
          ) : (
            <Text color="gray.500">채널 등록이 되어 있지 않습니다.</Text>
          )}

          {/* 영상 테이블 */}
          <Box mt={6}>
            <Flex mb={2} gap={2} align="center">
              <Input
                type="date"
                value={inputStartDate}
                onChange={(e) => setInputStartDate(e.target.value)}
                maxW="160px"
                bg="white"
              />
              <Text>~</Text>
              <Input
                type="date"
                value={inputEndDate}
                onChange={(e) => setInputEndDate(e.target.value)}
                maxW="160px"
                bg="white"
              />
              <Input
                placeholder="영상명 검색"
                value={inputSearch}
                onChange={(e) => setInputSearch(e.target.value)}
                maxW="300px"
                bg="white"
              />
              <Button colorScheme="teal" onClick={handleSearch}>
                검색
              </Button>
            </Flex>
            {channelVideosLoading ? (
              <Flex align="center" justify="center" minH="120px">
                <Spinner />
              </Flex>
            ) : (
              <Box overflowX="auto">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <thead style={{ background: "gray.600" }}>
                    <tr
                      style={{
                        height: 48,
                        borderRadius: 12,
                        marginTop: 8,
                        marginBottom: 8,
                        overflow: "hidden",
                      }}
                    >
                      <th
                        style={{
                          textAlign: "left",
                          paddingLeft: 12,
                          fontSize: 13,
                          wordBreak: "keep-all",
                          whiteSpace: "normal",
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        영상
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          fontSize: 13,
                          wordBreak: "keep-all",
                          whiteSpace: "normal",
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        업로드
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          fontSize: 13,
                          wordBreak: "keep-all",
                          whiteSpace: "normal",
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        조회수
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          fontSize: 13,
                          wordBreak: "keep-all",
                          whiteSpace: "normal",
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        US 예상 수익
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          fontSize: 13,
                          wordBreak: "keep-all",
                          whiteSpace: "normal",
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        KRW 예상 수익 (단가)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelVideos.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: "center",
                            color: "#888",
                            padding: 32,
                          }}
                        >
                          데이터가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      channelVideos.map((video, idx) => (
                        <tr
                          key={video.shorts_code}
                          style={{
                            borderBottom: "1px solid #eee",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(
                              `https://youtube.com/shorts/${video.shorts_code}`,
                              "_blank"
                            )
                          }
                        >
                          <td
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              paddingLeft: 12,
                            }}
                          >
                            <img
                              src={`https://img.youtube.com/vi/${video.shorts_code}/default.jpg`}
                              alt={video.shorts_title}
                              style={{
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                objectFit: "cover",
                              }}
                            />
                            <div>
                              <Text fontWeight="bold" fontSize="sm" ntSize="md">
                                {video.shorts_title}
                              </Text>
                              <Text fontSize="sm" color="gray.500">
                                {video.channel_name}
                              </Text>
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {video.upload_date}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {video.total_views?.toLocaleString()}
                            <br />
                            <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                              ({video.total_premium_views?.toLocaleString()}
                              {/*{video.premium_view_ratio}%*/})
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            $ {video.estimated_revenue_usd?.toLocaleString()}
                            <br />
                            <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                              {/*$ {video.usd_revenue_per_view}*/}
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            ₩ {video.estimated_revenue_krw?.toLocaleString()}
                            <br />
                            <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                              ₩ {video.krw_revenue_per_view}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
            {/* 페이지네이션 */}
            {channelVideosPagination && (
              <Flex justify="center" align="center" mt={4} gap={2}>
                <Button
                  onClick={() =>
                    setChannelVideosPage((p) => Math.max(1, p - 1))
                  }
                  isDisabled={channelVideosPage === 1}
                  size="sm"
                  variant="outline"
                >
                  이전
                </Button>
                <Text fontWeight="bold" mx={2}>
                  {channelVideosPage} / {channelVideosPagination.total_pages}
                </Text>
                <Button
                  onClick={() =>
                    setChannelVideosPage((p) =>
                      Math.min(channelVideosPagination.total_pages, p + 1)
                    )
                  }
                  isDisabled={
                    channelVideosPage === channelVideosPagination.total_pages
                  }
                  size="sm"
                  variant="outline"
                >
                  다음
                </Button>
              </Flex>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChannelDetailModal;
